import baseAPI from "@/api/base";
import {
  ForgotPassword,
  GoogleAuth,
  LoginCredentals,
  RegistrationCredentals,
  ResetPassword,
  ResetPasswordWithPhone,
} from "@/dtos/auth.dto";
import {
  FORGOT_PASSWORD_URL,
  GOOGLE_AUTH_URL,
  LOGIN_URL,
  RESET_PASSWORD_URL,
  RESET_PASSWORD_WITH_PHONE_URL,
  SIGNUP_URL,
  SET_PASSWORD_URL,
  USER_PROFILE_URL,
} from "@/utils/urls";
import { AxiosResponse } from "axios";
import { EditProfile } from "@/dtos/user.dto";

class Auth {
  static login = async (
    loginCredentals: LoginCredentals,
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(LOGIN_URL, {
      ...loginCredentals,
    });
    return response;
  };

  static signup = async (
    registrationCredentals: RegistrationCredentals,
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(SIGNUP_URL, {
      ...registrationCredentals,
    });
    return response;
  };

  // "Continue with Google" — verifies the ID token and signs the user in,
  // provisioning a brand-new account on first use (one endpoint for both).
  static googleAuth = async (body: GoogleAuth): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(GOOGLE_AUTH_URL, {
      ...body,
    });
    return response;
  };

  // Self-service profile update (PUT /user/profile).
  static updateAccount = async (
    editProfile: EditProfile,
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(USER_PROFILE_URL, {
      ...editProfile,
    });
    return response;
  };

  static forgotPassword = async (
    body: ForgotPassword,
  ): Promise<AxiosResponse> => {
    return baseAPI.post(FORGOT_PASSWORD_URL, { ...body });
  };

  // Reset password from the "forgot password" email link.
  static resetPassword = async (
    token: string,
    body: ResetPassword,
  ): Promise<AxiosResponse> => {
    return baseAPI.put(`${RESET_PASSWORD_URL}/${token}`, { ...body });
  };

  // Reset password after verifying the phone number via Firebase OTP. The
  // backend verifies the Firebase ID token and matches it to the account.
  static resetPasswordWithPhone = async (
    body: ResetPasswordWithPhone,
  ): Promise<AxiosResponse> => {
    return baseAPI.put(RESET_PASSWORD_WITH_PHONE_URL, { ...body });
  };

  // Staff invite: set the initial password and verify the email.
  static setPasswordAndVerify = async (
    token: string,
    body: ResetPassword,
  ): Promise<AxiosResponse> => {
    return baseAPI.put(`${SET_PASSWORD_URL}/${token}`, { ...body });
  };
}

export default Auth;
